/**
 * 카메라 표시 번호(분석 번호) 정렬 비교자.
 *
 * 명명 규칙(2026-08 협의): 마지막 2자리 = 카메라 인덱스(99 이하),
 * 그 앞 숫자 = 건물 층, 지하는 b 접두(층수 1자리).
 *
 * 정렬 규칙:
 * - 층은 건물 높이 기준 오름차순: b3 → b2 → b1 → 1 → 2 → …
 * - 같은 층에서는 카메라 인덱스 오름차순: b301 → b302 → b201 → …
 * - N 접두(비분석 번호)는 항상 분석 번호 뒤, 그 안에서는 숫자 오름차순
 * - 규칙 밖 문자열(숫자로 시작하지 않는 이름)은 분석 번호와 N 번호 사이
 *
 * 규칙은 소급 적용하지 않으므로, 규칙에 맞지 않는 기존 번호가
 * 의도와 다르게 정렬되는 것은 협의대로 감수한다.
 */

interface CamIdKey {
  /** 0 = 분석 번호, 1 = 규칙 밖 문자열, 2 = N 접두 비분석 번호 */
  group: number;
  /** 건물 층. 지하는 음수 (b3 → -3) */
  floor: number;
  /** 카메라 인덱스 */
  index: number;
}

function parseCamIdKey(raw: string): CamIdKey {
  if (raw.startsWith("N")) {
    const value = parseInt(raw.slice(1), 10);
    return { group: 2, floor: 0, index: Number.isNaN(value) ? 0 : value };
  }
  const m = /^([bB]?)(\d+)/.exec(raw);
  if (!m) return { group: 1, floor: 0, index: 0 };
  const digits = m[2];
  // 마지막 2자리 = 인덱스, 그 앞 = 층. 3자리 미만이면 층 없이 인덱스로만 취급
  const floorDigits = digits.length >= 3 ? digits.slice(0, -2) : "";
  const indexDigits = digits.length >= 3 ? digits.slice(-2) : digits;
  const floorNum = floorDigits === "" ? 0 : parseInt(floorDigits, 10);
  return {
    group: 0,
    floor: m[1] !== "" ? -floorNum : floorNum,
    index: parseInt(indexDigits, 10),
  };
}

export function compareCamId(a: string, b: string): number {
  const ka = parseCamIdKey(a);
  const kb = parseCamIdKey(b);
  if (ka.group !== kb.group) return ka.group - kb.group;
  if (ka.floor !== kb.floor) return ka.floor - kb.floor;
  if (ka.index !== kb.index) return ka.index - kb.index;
  // 동률(대시 접미 등 잔여 문자열 차이)은 자연 정렬로 안정화
  return a.localeCompare(b, undefined, { numeric: true });
}
